'use client'

import { GradeNotificationType } from '@gyym/sdk'
import { Button, cn, Image, ModalProps, Typography } from '@gyym/ui'
import { useRouter } from 'next/navigation'
import React from 'react'

import { Grade, GradeKey, passImages } from '@/modules/Grades'

import { gradeUpMessages } from './messages'
import {
  ArrowDownBackdrop,
  ArrowUpBackdrop,
  GradeBadge,
  StyledModal,
} from './styles'

export interface GyymPassModalProps
  extends Omit<ModalProps, 'children' | 'expandOnSmallDevices'> {
  grade: Grade
  type: GradeNotificationType
  isAfterProfileUpdate: boolean
  isButtonDisabled?: boolean
  onOpen?: () => void
}

const NEWLINE_REGEX = /(\r\n|\r|\n)/g

const nl2br = (text: string) => {
  return text
    .trim()
    .split(NEWLINE_REGEX)
    .map((line, index) => {
      if (line.match(NEWLINE_REGEX)) {
        return <br key={index} />
      }
      return line
    })
}

const POST_PROFILE_UPDATE_PATH = '/facilities?sortBy=distance&order=asc'

export const GyymPassModal = ({
  grade,
  type,
  onOpen,
  onClose,
  isAfterProfileUpdate,
  isButtonDisabled,
  ...props
}: GyymPassModalProps) => {
  const router = useRouter()
  const passImage = passImages[grade.key]
  const gradeUpMessage =
    grade.key !== GradeKey.GREENY ? nl2br(gradeUpMessages[grade.key] || '') : ''

  const isGradeUp = type === GradeNotificationType.GRADE_UP
  const isGradeDown = type === GradeNotificationType.GRADE_DOWN
  const isQuestClear = type === GradeNotificationType.ACHIEVEMENT
  const isWelcomeBack = type === GradeNotificationType.WELCOME_BACK

  const Backdrop = isGradeUp
    ? ArrowUpBackdrop
    : isGradeDown
    ? ArrowDownBackdrop
    : undefined

  const onCloseAndRedirect = React.useCallback(() => {
    onClose?.()

    if (isAfterProfileUpdate) {
      router.push(POST_PROFILE_UPDATE_PATH)
    }
  }, [onClose, isAfterProfileUpdate, router])

  const onConfirm = React.useCallback(() => {
    onClose?.()
    router.push('/me')
  }, [router, onClose])

  React.useEffect(() => {
    onOpen?.()
  }, [onOpen])

  return (
    <StyledModal
      {...props}
      // eslint-disable-next-line tailwindcss/no-custom-classname
      className={
        isGradeUp
          ? 'grade-up'
          : isGradeDown
          ? 'grade-down'
          : isQuestClear
          ? 'quest-clear'
          : undefined
      }
      onClose={onCloseAndRedirect}
      disableBackdropClose={props.disableBackdropClose ?? true}
      BackdropComponent={Backdrop}
    >
      {(isGradeUp || isGradeDown || isWelcomeBack) && (
        <>
          <div
            className={cn(
              'relative flex w-full items-center justify-center',
              !isWelcomeBack ? 'mt-20' : 'mt-12',
            )}
          >
            {(isGradeUp || isGradeDown) && (
              <div className="absolute left-[26px] top-[-50px] z-[1] mt-4 w-fit">
                {isGradeUp && (
                  // eslint-disable-next-line tailwindcss/no-custom-classname
                  <GradeBadge className="bump">
                    <Image
                      src="/assets/images/pass/modal_badge_grade_up.png"
                      alt="RANK UP"
                      width={126 / 2}
                      height={152 / 2}
                    />
                  </GradeBadge>
                )}

                {isGradeDown && (
                  // eslint-disable-next-line tailwindcss/no-custom-classname
                  <GradeBadge className="shrink-down">
                    <Image
                      src="/assets/images/pass/modal_badge_grade_down.png"
                      alt="RANK DOWN"
                      width={126 / 2}
                      height={152 / 2}
                    />
                  </GradeBadge>
                )}
              </div>
            )}
            <Image
              src={passImage}
              alt={grade.name}
              width={192}
              height={77.78}
            />
          </div>

          <h3
            className={cn(
              'flex items-center justify-center',
              isGradeUp && 'mt-4 px-[72px]',
              isGradeDown && 'mt-6 px-[88px]',
              isWelcomeBack && 'mt-4',
            )}
          >
            {isGradeUp && (
              <Image
                src="/assets/images/pass/modal_title_grade_up.png"
                alt="CONGRATULATIONS"
                width={364 / 2}
                height={30 / 2}
              />
            )}

            {isGradeDown && (
              <Image
                src="/assets/images/pass/modal_title_grade_down.png"
                alt="sorry..."
                width={152 / 2}
                height={34 / 2}
              />
            )}

            {isWelcomeBack && (
              <div className="flex w-full items-center justify-center">
                <Typography color="red.01" variant="headline1">
                  お帰りなさい！！
                </Typography>
              </div>
            )}
          </h3>
        </>
      )}

      {isQuestClear && (
        <div className="relative mx-6 mt-[88px] flex flex-col items-center justify-center rounded-lg bg-gradient-primary py-5 text-white tablet:mx-10 pc:mx-10">
          <Typography variant="subhead2">今月のGYYM PASSクエスト</Typography>
          <div className="mt-2">
            <Typography variant="headline2">
              予約{grade.monthlyReservationNumToAchieve}回 達成！
            </Typography>
          </div>
        </div>
      )}

      {isGradeUp && !isAfterProfileUpdate && (
        <p className="mt-5 px-8">
          <Typography variant="caption1">{gradeUpMessage}</Typography>
        </p>
      )}

      {isGradeUp && isAfterProfileUpdate && (
        <p className="mt-5 px-8">
          <Typography variant="caption1">
            Welcome to GYYM 🎉
            <br />
            今日からあなたの「健やかな毎日」がはじまります。
            <br />
            フィットネスで身体を動かす日も、ヨガやピラティスで自分と向き合う日も、サウナや整体でじっくり整える日も。
            <br />
            気分に合わせて、心と身体に心地よい時間を選んでみてください 🌿
          </Typography>
        </p>
      )}

      {isGradeDown && (
        <p className="mt-5 px-8">
          <Typography variant="caption1">
            あなたのGYYM PASSが{grade.name}にランクダウンしました。
            <br />
            今月はランクアップ目指して、また一緒に楽しくフィットネスを頑張りましょう📣
          </Typography>
        </p>
      )}

      {isWelcomeBack && (
        <p className="mt-5 px-8">
          <Typography variant="caption1">
            あなたの現在のGYYM PASSは{grade.name}です。
            <br />
            お帰りなさい、また一緒に頑張りましょう📣
            まずは、無理なく月1ペースからでも継続することが大切です🌟
          </Typography>
        </p>
      )}

      {isQuestClear && (
        <p className="mt-5 px-8">
          <Typography variant="caption1">
            おめでとうございます 🎉
            <br />
            今月のクエストを達成しました。来月のランクアップが楽しみですね😄
          </Typography>
        </p>
      )}

      <div
        className={cn(
          'mt-6 flex w-full justify-center self-center px-12',
          !isWelcomeBack ? 'mb-16' : 'mb-10',
        )}
      >
        {isAfterProfileUpdate ? (
          <Button
            variant="primary-outlined"
            onClick={onCloseAndRedirect}
            disabled={isButtonDisabled}
          >
            閉じる
          </Button>
        ) : (
          <Button
            variant="primary-outlined"
            fullWidth
            onClick={onConfirm}
            disabled={isButtonDisabled}
          >
            {(isGradeUp || isGradeDown) && 'GYYM PASSを確認する'}
            {isWelcomeBack && '現在のPASSを確認する'}
            {isQuestClear && 'マイページで確認する'}
          </Button>
        )}
      </div>
    </StyledModal>
  )
}
